import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Search, Loader2, ArrowRight, Mail, BellRing, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InquiryDialog } from "@/components/InquiryDialog";
import { SaveSearchDialog } from "@/components/SaveSearchDialog";
import { useTour } from "@/components/tour/TourProvider";
import { usePageMeta } from "@/lib/seo";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { SiteNav, SiteFooter } from "@/components/site/SiteChrome";
import { useSpanishScreens } from "@/lib/i18n";

const WALL_META = {
  title: "Peer Verified Businesses | GrantVerify Public Badge Wall",
  description: "Browse small businesses that earned the Peer Verified badge from GrantVerify by Peer Verify LLC after completing grant-ready proposals, budgets and state licensing checks.",
  path: "/verified",
};

export default function BadgeWall() {
  usePageMeta(WALL_META);
  const [state, setState] = useState("all");
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("all");
  const [days, setDays] = useState("0");
  const [d, setD] = useState(null);
  const [contact, setContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const { start: startTour, offerTour, lang } = useTour();
  useSpanishScreens(lang === "es");
  useEffect(() => { const t = setTimeout(() => offerTour("wall"), 2500); return () => clearTimeout(t); }, [offerTour]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/badge-wall", { params: { state: state === "all" ? "" : state, q, industry: industry === "all" ? "" : industry, days } }).then((r) => setD(r.data)), 250);
    return () => clearTimeout(t);
  }, [state, q, industry, days]);
  return (
    <div className="min-h-screen bg-slate-50">
      <SiteNav extra={<button onClick={() => startTour("wall")} className="hidden xl:flex text-sm font-semibold text-emerald-700 whitespace-nowrap hover:text-emerald-800 items-center gap-1.5" data-testid="badge-wall-meet-vera"><Sparkles className="h-4 w-4" />{lang === "es" ? "Conozca a Vera" : "Meet Vera"}</button>} />
      <section className="bg-slate-900 text-white grid-bg" style={{ backgroundBlendMode: "overlay" }}>
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-amber-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Peer Verified directory</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 max-w-3xl" data-testid="badge-wall-title">Businesses verified licensed and grant-ready</h1>
          <p className="text-slate-300 mt-4 max-w-2xl">Each business below completed its state licensing path and had a proposal approved by a certified human editor in the Peer Verify LLC network. Lenders, landlords and agencies can open any certificate to confirm it.</p>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" /><Input className="pl-9 bg-white" placeholder="Search business or industry" value={q} onChange={(e) => setQ(e.target.value)} data-testid="badge-wall-search-input" /></div>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="sm:w-56 bg-white" data-testid="badge-wall-state-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All states</SelectItem>{(d?.states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="sm:w-56 bg-white" data-testid="badge-wall-industry-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All industries</SelectItem>{(d?.industries || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="badge-wall-date-filter">
          {[["0", "Any time"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["365", "Last year"]].map(([v, l]) => (
            <button key={v} onClick={() => setDays(v)} data-testid={`badge-wall-days-${v}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-150 ${days === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>Verified {l.toLowerCase()}</button>
          ))}
          <Button size="sm" onClick={() => setSaving(true)} className="ml-auto rounded-full bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="badge-wall-save-search-button"><BellRing className="h-3.5 w-3.5 mr-1.5" />Email me new matches</Button>
        </div>
        {!d ? <Loader2 className="h-6 w-6 animate-spin text-emerald-600 mt-10" /> : d.items.length === 0 ? (
          <p className="text-slate-500 mt-10" data-testid="badge-wall-empty">No verified businesses match these filters yet.</p>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="badge-wall-grid">
            {d.items.map((b) => (
              <div key={b.badge_id} className="card-lift bg-white border border-slate-200 border-t-4 border-t-amber-500 rounded-lg p-6 flex flex-col" data-testid={`badge-wall-card-${b.badge_id}`}>
                <Link to={`/verify/${b.badge_id}`} className="block flex-1" data-testid={`badge-wall-link-${b.badge_id}`}>
                  <ShieldCheck className="h-6 w-6 text-amber-500" />
                  <h3 className="font-display text-xl font-bold text-slate-900 mt-3">{b.company}</h3>
                  <p className="text-sm text-slate-500 mt-1">{[b.industry, b.entity_type].filter(Boolean).join(" · ")}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                    <span>{b.state || "—"} · verified {new Date(b.issued_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</span>
                    <span className="text-emerald-700 font-semibold flex items-center gap-1">Certificate <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
                <Button size="sm" variant="outline" onClick={() => setContact(b)} className="mt-4 w-full border-slate-300 hover:border-emerald-600 hover:text-emerald-700" data-testid={`badge-wall-contact-${b.badge_id}`}><Mail className="h-4 w-4 mr-1.5" />Contact this business</Button>
              </div>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
      {saving && <SaveSearchDialog filters={{ state: state === "all" ? "" : state, industry: industry === "all" ? "" : industry, q }} onClose={() => setSaving(false)} />}
      {contact && <InquiryDialog badge={contact} onClose={() => setContact(null)} />}
    </div>
  );
}
